// Comply · Dashboard — compliance command centre.
//
// Thin renderer over the server-composed `/api/comply/dashboard` payload
// (KPIs, next regulatory deadline, alerts feed, attention items). Every figure
// is real ontology data read through `ctx.read` server-side; there is no
// fabricated fallback (ADR-0005). When a source is empty the relevant card
// renders an honest empty state rather than sample rows.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ComplyAlertFeedItem, ComplyDashboardResponse, OdCashRowDto } from '@septima/contract';
import { PageHeader, Card, Stat, Pill, DataTable } from '@/components/ui';
import type { DataTableColumn, PillVariant, StatTone } from '@/components/ui';
import EmptyState from '@/components/EmptyState';
import { api, apiFetch } from '@/lib/api';
import { formatSignedCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';

interface DirectoryUser {
    id: string;
    name: string | null;
    email: string;
}

type Rag = 'Green' | 'Amber' | 'Red';

function ragTone(rag: Rag): StatTone {
    switch (rag) {
        case 'Green':
            return 'positive';
        case 'Amber':
            return 'warning';
        case 'Red':
            return 'negative';
    }
}

function severityVariant(severity: string): PillVariant {
    switch (severity) {
        case 'High':
        case 'Material':
            return 'red';
        case 'Medium':
        case 'Potential material':
            return 'amber';
        default:
            return 'grey';
    }
}

const ALERT_COLUMNS: DataTableColumn<ComplyAlertFeedItem>[] = [
    {
        id: 'date',
        header: 'Date',
        numeric: true,
        minWidth: '110px',
        sortable: true,
        sortValue: (a) => a.date,
        cell: (a) => <span className="text-[rgb(var(--color-text-secondary))]">{a.date}</span>,
    },
    {
        id: 'type',
        header: 'Type',
        minWidth: '140px',
        sortable: true,
        sortValue: (a) => a.type,
        cell: (a) => <span className="text-[rgb(var(--color-text-primary))]">{a.type}</span>,
    },
    {
        id: 'client',
        header: 'Client',
        minWidth: '160px',
        sortable: true,
        sortValue: (a) => a.client.toLowerCase(),
        cell: (a) => <span className="text-[rgb(var(--color-text-secondary))]">{a.client}</span>,
    },
    {
        id: 'description',
        header: 'Description',
        minWidth: '240px',
        cell: (a) => (
            <span className="text-[rgb(var(--color-text-secondary))]">{a.description}</span>
        ),
    },
    {
        id: 'severity',
        header: 'Severity',
        align: 'center',
        minWidth: '110px',
        sortable: true,
        sortValue: (a) => a.severity,
        cell: (a) => <Pill variant={severityVariant(a.severity)} label={a.severity} size="sm" />,
    },
    {
        id: 'status',
        header: 'Status',
        align: 'center',
        minWidth: '130px',
        cell: (a) => <Pill variant="grey" label={a.status} size="sm" />,
    },
];

export default function ComplyDashboardPage() {
    const { data, isLoading } = useQuery<ComplyDashboardResponse>({
        queryKey: ['comply', 'dashboard'],
        queryFn: async () => {
            const res = await api.complyDashboard.get();
            if (res.status !== 200) throw new Error(`comply-dashboard fetch failed: ${res.status}`);
            return res.body;
        },
        refetchInterval: 30_000,
    });

    // Overdrawn-cash rows Ops has escalated to Compliance (SEP-15). Read-only:
    // resolving is an Ops act and drops the row off this list.
    const escalationsQuery = useQuery({
        queryKey: ['comply', 'cash', 'overdrawn-escalations'],
        queryFn: async () => {
            const res = await api.cash.listOverdrawnEscalationsForComply({ query: {} });
            if (res.status !== 200)
                throw new Error(`overdrawn-escalations fetch failed: ${res.status}`);
            return res.body;
        },
        refetchInterval: 30_000,
    });
    const escalatedRows = useMemo<OdCashRowDto[]>(
        () => [...(escalationsQuery.data?.rows ?? [])],
        [escalationsQuery.data],
    );

    // Directory users to render the escalating operator's display name —
    // same pattern as the Ops overdrawn page.
    const usersQuery = useQuery({
        queryKey: ['users'],
        queryFn: async (): Promise<DirectoryUser[]> => {
            const res = await apiFetch('/api/users');
            if (!res.ok) return [];
            return ((await res.json()) as { users: DirectoryUser[] }).users;
        },
    });
    const userById = useMemo(() => {
        const m = new Map<string, DirectoryUser>();
        for (const u of usersQuery.data ?? []) m.set(u.id, u);
        return m;
    }, [usersQuery.data]);

    const escalationColumns = useMemo<DataTableColumn<OdCashRowDto>[]>(
        () => [
            {
                id: 'client',
                header: 'Client',
                minWidth: '130px',
                sortable: true,
                sortValue: (row) => row.clientReference,
                cell: (row) => (
                    <span className="font-mono tabular-nums text-[rgb(var(--color-text-primary))] truncate">
                        {row.clientReference}
                    </span>
                ),
            },
            {
                id: 'account',
                header: 'Account',
                minWidth: '140px',
                cell: (row) => (
                    <span className="font-mono tabular-nums text-[rgb(var(--color-text-secondary))] truncate">
                        {row.accountReference}
                    </span>
                ),
            },
            {
                id: 'postedBalance',
                header: 'Posted Balance',
                numeric: true,
                minWidth: '130px',
                sortable: true,
                sortValue: (row) => row.postedBalance,
                cell: (row) => (
                    <span className="font-medium text-[rgb(var(--color-rag-red))]">
                        {formatSignedCurrency(row.postedBalance, row.currency)}
                    </span>
                ),
            },
            {
                id: 'im',
                header: 'IM',
                minWidth: '140px',
                cell: (row) => {
                    const matched = row.matchedUserId
                        ? (userById.get(row.matchedUserId) ?? null)
                        : null;
                    return (
                        <span className="text-[rgb(var(--color-text-secondary))] truncate">
                            {matched
                                ? (matched.name ?? matched.email)
                                : row.investmentManager || '—'}
                        </span>
                    );
                },
            },
            {
                id: 'escalatedAt',
                header: 'Escalated',
                minWidth: '110px',
                sortable: true,
                sortValue: (row) => row.escalatedAt ?? '',
                cell: (row) => (
                    <span className="font-mono tabular-nums text-[rgb(var(--color-text-secondary))]">
                        {row.escalatedAt
                            ? new Date(row.escalatedAt).toLocaleDateString('en-GB')
                            : '—'}
                    </span>
                ),
            },
            {
                id: 'note',
                header: 'Escalation note',
                minWidth: '220px',
                cell: (row) => (
                    <span className="text-[rgb(var(--color-text-secondary))]">
                        {row.escalationNote ?? '—'}
                    </span>
                ),
            },
            {
                id: 'escalatedBy',
                header: 'Escalated by',
                minWidth: '140px',
                cell: (row) => {
                    const by = row.escalatedBy ? (userById.get(row.escalatedBy) ?? null) : null;
                    return (
                        <span className="text-[rgb(var(--color-text-secondary))] truncate">
                            {by ? (by.name ?? by.email) : (row.escalatedBy ?? '—')}
                        </span>
                    );
                },
            },
        ],
        [userById],
    );

    const kpis = data?.kpis;
    const openCount = data?.openCount ?? 0;
    const nextDeadline = data?.nextDeadline ?? null;
    const alertsFeed = useMemo<ComplyAlertFeedItem[]>(() => data?.alertsFeed ?? [], [data]);
    const attentionItems = useMemo(() => data?.attentionItems ?? [], [data]);

    // A6: suppress the KPI strip when no figure is genuinely populated — every
    // tile would read 0 / —, conveying nothing the empty cards below don't.
    const hasKpiData =
        !!kpis &&
        (kpis.openBreaches > 0 ||
            kpis.totalOverdue > 0 ||
            kpis.avgConsumerDuty > 0 ||
            kpis.capitalRatio > 0);

    return (
        <div className="flex flex-col h-full">
            <PageHeader
                title="Dashboard"
                subtitle="Live view of breaches, reviews, capital posture and upcoming returns."
            />

            <div className="flex-1 overflow-y-auto">
                <div className="max-w-content mx-auto flex flex-col gap-section-gap px-content-padding pt-7 pb-bottom-padding">
                    {hasKpiData && kpis && (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-card-gap">
                            <Stat
                                label="Open breaches"
                                value={kpis.openBreaches.toString()}
                                detail={`${kpis.materialOpen} material, ${kpis.nonMaterialOpen} non-material`}
                                tone={ragTone(kpis.breachRAG)}
                            />
                            <Stat
                                label="Overdue reviews"
                                value={kpis.totalOverdue.toString()}
                                detail={`${kpis.overdueKYC} KYC, ${kpis.overdueCMP} CMP`}
                                tone={ragTone(kpis.overdueRAG)}
                            />
                            <Stat
                                label="Consumer Duty"
                                value={kpis.avgConsumerDuty > 0 ? `${kpis.avgConsumerDuty}%` : '—'}
                                detail="Firm average across four pillars"
                                tone={
                                    kpis.avgConsumerDuty > 0
                                        ? ragTone(kpis.consumerDutyRAG)
                                        : 'neutral'
                                }
                            />
                            <Stat
                                label="Capital ratio"
                                value={
                                    kpis.capitalRatio > 0 ? `${kpis.capitalRatio.toFixed(2)}x` : '—'
                                }
                                detail="Own funds against requirement"
                                tone={kpis.capitalRatio > 0 ? ragTone(kpis.capitalRAG) : 'neutral'}
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-card-gap">
                        <div className="lg:col-span-2">
                            <Card
                                title="Exception feed"
                                actions={
                                    <Pill
                                        variant={alertsFeed.length > 0 ? 'amber' : 'green'}
                                        label={
                                            alertsFeed.length > 0
                                                ? `${alertsFeed.length} open`
                                                : 'Clear'
                                        }
                                        size="sm"
                                    />
                                }
                                noPadding
                            >
                                <DataTable<ComplyAlertFeedItem>
                                    ariaLabel="Exception feed"
                                    columns={ALERT_COLUMNS}
                                    data={alertsFeed}
                                    rowKey={(a) => a.id}
                                    loading={isLoading}
                                    initialSort={{ id: 'date', desc: true }}
                                    empty={<EmptyState title="No active alerts" />}
                                />
                            </Card>
                        </div>

                        <Card title={nextDeadline?.name ?? 'Next deadline'}>
                            {nextDeadline ? (
                                <div className="flex flex-col gap-3">
                                    <span className="text-secondary-metric font-mono tabular-nums text-[rgb(var(--color-text-primary))]">
                                        {nextDeadline.dueDate}
                                    </span>
                                    <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                                        {nextDeadline.authority} · {nextDeadline.period}
                                        {nextDeadline.preparer
                                            ? ` · Preparer: ${nextDeadline.preparer}`
                                            : ''}
                                    </p>
                                    <Pill variant="grey" label={nextDeadline.status} size="sm" />
                                </div>
                            ) : (
                                <EmptyState
                                    title="Regulatory calendar clear"
                                    body="No imminent return is outstanding."
                                />
                            )}
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 gap-card-gap">
                        <Card
                            title="Overdrawn cash escalations"
                            actions={
                                <Pill
                                    variant={escalatedRows.length > 0 ? 'red' : 'green'}
                                    label={
                                        escalatedRows.length > 0
                                            ? `${escalatedRows.length} escalated`
                                            : 'Clear'
                                    }
                                    size="sm"
                                />
                            }
                            noPadding
                        >
                            <DataTable<OdCashRowDto>
                                ariaLabel="Overdrawn cash escalations"
                                columns={escalationColumns}
                                data={escalatedRows}
                                rowKey={(row) => row.id}
                                loading={escalationsQuery.isLoading}
                                initialSort={{ id: 'escalatedAt', desc: true }}
                                empty={<EmptyState title="No escalated overdrawn-cash rows" />}
                            />
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 gap-card-gap">
                        <Card
                            title="Attention required"
                            actions={
                                <Pill
                                    variant={openCount > 0 ? 'amber' : 'green'}
                                    label={openCount > 0 ? `${openCount} open` : 'No open items'}
                                    size="sm"
                                />
                            }
                        >
                            {attentionItems.length > 0 ? (
                                <ul className="flex flex-col">
                                    {attentionItems.map((item) => (
                                        <li
                                            key={item.id}
                                            className="flex items-center gap-3 py-2.5 border-b border-[rgb(var(--color-divider)/0.6)] last:border-b-0"
                                        >
                                            <span
                                                className={cn(
                                                    'h-2 w-2 flex-none rounded-full',
                                                    item.ragVariant === 'red'
                                                        ? 'bg-[rgb(var(--color-rag-red))]'
                                                        : item.ragVariant === 'amber'
                                                          ? 'bg-[rgb(var(--color-rag-amber))]'
                                                          : 'bg-[rgb(var(--color-rag-green))]',
                                                )}
                                            />
                                            <span className="min-w-0 flex-1 truncate text-table-body text-[rgb(var(--color-text-primary))]">
                                                {item.label}
                                            </span>
                                            <span className="flex-none text-annotation font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                                                {item.age}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <EmptyState title="Nothing requires attention" />
                            )}
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
